import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiX, FiHeart, FiUser, FiMail, FiPhone, FiGift, FiDollarSign, FiMessageSquare } from "react-icons/fi";
import { apiRequest, errorMessage } from "../../api";
import { HELP_TYPES } from "./foundationUtils";
import { Field, FormAlert, SubmitButton, SuccessPanel } from "../forms/formKit";

// "Help this person" window: a generous visitor offers money, goods or time. The foundation team
// confirms it and coordinates - the offer is not shown publicly until an admin confirms it.
export default function HelpModal({ item, settings, onClose, onDone }) {
  const currency = settings?.currency_label || "PKR";
  const [form, setForm] = useState({ helper_name: "", email: "", phone: "", help_type: "money", amount: "", message: "", is_anonymous: false });
  const [status, setStatus] = useState("idle");   // idle | busy | done
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("busy");
    setError("");
    const { ok, data } = await apiRequest("/core/foundation/offers/", {
      method: "POST",
      body: { ...form, case: item.id, amount: form.help_type === "money" && form.amount !== "" ? form.amount : 0 },
    });
    if (!ok) {
      setStatus("idle");
      return setError(errorMessage(data));
    }
    setStatus("done");
    onDone?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="fx-card fx-card-static relative w-full max-w-lg my-auto"
      >
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-white"><FiX size={20} /></button>

        {status === "done" ? (
          <SuccessPanel title="Thank you!" action="Close" onAction={onClose}>
            Your offer to help <span className="text-accent-light">{item.display_name}</span> was received. Our team will contact you shortly and explain how to send your help safely.
          </SuccessPanel>
        ) : (
          <>
            <h3 className="text-2xl font-bold flex items-center gap-2 pr-8"><FiHeart className="text-accent-light" /> Help {item.display_name}</h3>
            <p className="text-sm text-gray-400 mt-1">{item.title}</p>

            {settings?.help_instructions && (
              <div className="mt-4 rounded-xl bg-accent/10 border border-accent-light/30 p-3 text-sm text-gray-200 whitespace-pre-line">
                {settings.help_instructions}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4 mt-5">
              <Field label="Your name" required icon={FiUser} placeholder="e.g. Ali Khan" autoComplete="name" value={form.helper_name} onChange={set("helper_name")} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email" icon={FiMail} type="email" placeholder="you@example.com" autoComplete="email" value={form.email} onChange={set("email")} />
                <Field label="Phone" icon={FiPhone} placeholder="With country code" autoComplete="tel" value={form.phone} onChange={set("phone")} />
              </div>
              <p className="text-xs text-gray-500 -mt-2">Give at least one - only our team sees your contact details.</p>

              <div className="grid grid-cols-2 gap-4">
                <Field as="select" label="Type of help" icon={FiGift} value={form.help_type} onChange={set("help_type")}>
                  {HELP_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </Field>
                {form.help_type === "money" ? (
                  <Field label={`Amount (${currency})`} required icon={FiDollarSign} type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} />
                ) : (
                  <div />
                )}
              </div>

              <Field as="textarea" label="Message" icon={FiMessageSquare} rows={3} hint="Optional - what would you like to give, or a kind word" value={form.message} onChange={set("message")} />

              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" className="accent-cyan-400 w-4 h-4" checked={form.is_anonymous} onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })} />
                Keep my name private on the public page
              </label>

              <FormAlert>{error}</FormAlert>
              <SubmitButton busy={status === "busy"} icon={FiHeart} className="w-full">Offer to Help</SubmitButton>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
